/**
 * Same-origin BFF helpers for the signed-in user.
 *
 * These hit /bff/* on console.wardkey.app — the thin BFF Worker that holds the
 * session in an HttpOnly cookie. They are intentionally separate from the
 * cross-origin WardkeyClient (which talks to api.wardkey.app) because the BFF
 * surface is private to the console.
 */

export type Impersonation = {
  staff_user_id: string;
  staff_email: string;
  target_user_id: string;
  /** Unix ms the supervised session ends. */
  expires_at: number;
};

export type Me = {
  user_id: string;
  email: string;
  provider: "github" | "google" | "email";
  /** `null` (not missing) when the user has no display name on file. */
  name: string | null;
  /** `null` (not missing) when the user has no avatar on file. */
  avatar_url: string | null;
  /**
   * Present only while a Wardkey staff operator is acting on this user's
   * behalf (WARD-47). Drives the non-dismissible banner in the shell.
   */
  impersonation?: Impersonation;
};

export class UnauthorizedError extends Error {
  constructor() {
    super("unauthorized");
    this.name = "UnauthorizedError";
  }
}

export async function fetchMe(signal?: AbortSignal): Promise<Me> {
  const init: RequestInit = {
    headers: { accept: "application/json" },
    credentials: "same-origin",
    ...(signal ? { signal } : {}),
  };
  const res = await fetch("/bff/me", init);
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) throw new Error(`/bff/me failed: ${res.status}`);
  return (await res.json()) as Me;
}

export async function logout(): Promise<void> {
  const res = await fetch("/bff/auth/logout", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error("Sign out failed");
}

type DisplayNameInput = { email: string; name?: string | null };

export function displayName(me: DisplayNameInput): string {
  if (me.name?.trim()) return me.name.trim();
  const at = me.email.indexOf("@");
  return at > 0 ? me.email.slice(0, at) : me.email;
}

export function firstName(me: DisplayNameInput): string {
  const full = displayName(me);
  return full.split(/\s+/)[0] ?? full;
}
